import { useState, useEffect } from "react";
import styled from "styled-components";

import Dialog from "src/components/core/Dialog";
import Button from "src/components/core/Button";

import {
  Stack,
  IconButton,
  Typography,
  TextField,
  Autocomplete,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import { DialogTitle, DialogContent, DialogActions } from "@mui/material";

import { createFilterOptions } from "@mui/material";
import { convertPrice } from "src/common";

import { useDialog } from "src/context/DialogContext";
import { useSnackbar } from "notistack";

import { arrayMove } from "@dnd-kit/sortable";
import DraggableBoard, {
  DraggableItem,
} from "src/components/core/DraggableBoard";

import font from "src/styles/font";
import { hexToRgba } from "src/common/color";
import { Black, DarkGrey, White } from "src/styles/color";
import { getLocale } from "react-i18nify";
import { searchProduct } from "src/api/product";

import ProductCard from "./ProductCard";

import { MAX_PRODUCT_SUGGESTION } from "./ProductSuggestion";
import { getProductSuggest, updateProductSuggest } from "src/api/landingPage";

const S = {};

const ImageEmpty = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #f2f1f3;
  border-radius: 8px;
  object-fit: cover;
  object-position: center;
  cursor: pointer;
  vertical-align: initial;
  margin-right: 16px;
  &:hover {
    opacity: 0.8;
  }

  &.active {
    border: 2px solid #707070;
  }
`;

S.ProductSuggestionContainer = styled.div`
  width: fit-content;
  display: grid;
  grid-template-columns: repeat(5, minmax(0px, 1fr));
  gap: 16px;
  margin: 0 auto;
`;

const WrapperProductCard = styled(DraggableItem)`
  width: 100%;
  position: relative;
  max-width: 200px;
`;

S.ProductOptionContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(12, 1fr);
`;

S.HoverContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  opacity: 0;
  transition: all 0.2s;

  &:hover {
    opacity: 1;
  }
`;

S.ItemBackdrop = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${hexToRgba(Black, 0.5)};
  border-radius: 16px;
`;

S.ItemActionMenu = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  padding: 8px;
`;

const formatFromElasticToProductSuggestion = (data) => {
  const format = {
    _id: data.id,
    item_name: data.item_name,
    item_code: data.item_code,
    description: data.description,
    status: data.status,
    suitable_areas: data.area.split(",").map((el, index) => ({
      _id: el,
      area_en: data.area_en.split(",")[index],
      area_th: data.area_th.split(",")[index],
    })),
    category: {
      _id: data.categories,
      cate_en: data.categories_en,
      cate_th: data.categories_th,
    },
    user: {
      _id: data.user,
      seller: {
        company_name: data.company_name,
        profile_image: data.profile_image,
        is_delivery: data.is_delivery,
        is_sell: data.is_sell,
      },
    },
    brand: {
      _id: data.brand_id,
      name: data.brand,
    },
    hashtags: data.hashtag.split(",").map((el) => ({
      _id: null,
      name: el,
    })),
    product_types: data.product_type.split(",").map((el, index) => ({
      _id: el,
      type_en: data.product_type_en.split(",")[index],
      type_th: data.product_type_th.split(",")[index],
    })),
    pattern: {
      _id: data.pattern,
      pattern_en: data.pattern_en,
      pattern_th: data.pattern_th,
    },
    is_edit: data.is_edit,
    is_public: data.is_public,
    is_installer: data.is_installer,
    product_choices: {
      _id: data.id,
      name: data.name,
      color: {
        _id: data.color,
        color_en: data.color_en,
        color_th: data.color_th,
      },
      texture_type: {
        _id: data.texture,
        type_th: data.texture_type_th,
        type_en: data.texture_type_en,
      },
      is_deal: data.is_deal,
      is_instock: data.is_instock,
      stock_count: data?.stock_count,
      stock_date: data?.stock_date,
      view_count: data.view_count,
      group_price: {
        reduce_percent: data.reduce_percent,
        unit_price: {
          unit_th: data.unit_price_th,
          unit_en: data.unit_price_en,
        },
        price: data.price,
        special_price: data.special_price,
        start_price: data.start_price,
        is_start_price: data.is_start_price,
        install_price: data.install_price || 0,
        sample_price: data.sample_price || 0,
        price_per_sq: data.price_per_sq || 0,
      },
      group_image: {
        product_full_image: data.product_full_image,
        product_closeup_image: data.product_image,
        product_display_image: data.display_img,
      },
      size: {
        calculate_size: data.calculate_size || null,
        width: data.width,
        height: data.height,
        thickness: data.thickness,
        quantity: data.quantity,
        unit_width: {
          unit_th: data.unit_width_th,
          unit_en: data.unit_width_en,
        },
        unit_height: {
          unit_th: data.unit_height_th,
          unit_en: data.unit_height_en,
        },
        unit_thickness: {
          unit_th: data.unit_thickness_th,
          unit_en: data.unit_thickness_en,
        },
        capacity_size: data.capacity_size,
        unit_capacity: {
          unit_th: data.unit_capacity_th,
          unit_en: data.unit_capacity_en,
        },
      },
      hashtags: data.hashtag.split(",").map((el) => ({
        _id: null,
        name: el,
      })),
    },
  };
  return format;
};

const ProductSuggestionAddDialog = ({ onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { openDialog, closeDialog } = useDialog();

  const [productList, setProductList] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const fetchProductSuggest = async () => {
    try {
      const result = await getProductSuggest();
      setSelectedProducts(result);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchProductSuggest();
  }, []);

  const addProduct = (product) => {
    if (product) {
      setSelectedProducts((prev) => {
        if (prev.length < MAX_PRODUCT_SUGGESTION) {
          return [...prev, product];
        } else {
          enqueueSnackbar(
            `คุณได้เพิ่มสินค้าครบ ${MAX_PRODUCT_SUGGESTION} รายการแล้ว`,
            {
              variant: "error",
            },
          );
          return prev;
        }
      });
    }
  };

  const removeProduct = (id) => {
    setSelectedProducts((prev) => {
      return prev.filter((item) => item._id !== id);
    });
  };

  const handleSubmit = async () => {
    try {
      await updateProductSuggest({
        product_choices: selectedProducts.map(
          (product) => product?.product_choices?._id,
        ),
      });

      enqueueSnackbar(`แก้ไขสำเร็จ`, {
        variant: "success",
      });

      onSuccess();
    } catch (err) {
      console.error(err);
      closeDialog();
    }
  };

  useEffect(() => {
    (async () => {
      await searchProduct({}).then(async ({ data }) => {
        setProductList(
          data?.rows.length > 0
            ? data?.rows?.map((product) => ({
                ...formatFromElasticToProductSuggestion(product),
              }))
            : [],
        );
      });
    })();
  }, []);

  return (
    <Dialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={true}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: 1280,
            maxWidth: "100%",
          },
        },
        "& .MuiDialogContent-root": {
          pt: 1,
          pb: 4,
        },
      }}
    >
      <DialogTitle sx={{ p: 3 }}>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack width="100%" alignItems="center">
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
            }}
          >
            แก้ไขรายการสินค้าโปรโมชั่น
          </Typography>

          <Autocomplete
            aria-required={true}
            id="combo-box-similars"
            getOptionSelected={(option, value) => option._id === value._id}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            renderOption={(props, option, { selected }) => {
              const { className } = props; // destruct only className, and create custom auto complete behavior
              return (
                <li
                  className={className}
                  key={`item-${option?._id}`}
                  style={{
                    cursor: "unset", // disable hover effect
                  }}
                >
                  <S.ProductOptionContainer>
                    <Box
                      sx={{
                        gridColumn: "1 / 4",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {!!option?.product_choices?.group_image
                        ?.product_display_image === false ? (
                        <ImageEmpty>
                          <InsertPhotoIcon
                            sx={{
                              color: "rgba(0, 0, 0, 0.54)",
                              cursor: "pointer",
                            }}
                          />
                        </ImageEmpty>
                      ) : (
                        <img
                          style={{
                            width: "40px",
                            height: "40px",
                            objectFit: "cover",
                            objectPosition: "center",
                            borderRadius: "8px",
                            marginRight: "16px",
                          }}
                          src={
                            option?.product_choices?.group_image
                              ?.product_display_image
                              ? option?.product_choices?.group_image?.product_display_image.replace(
                                  ".com",
                                  ".com/300x300",
                                )
                              : ""
                          }
                          alt={
                            option?.product_choices?.group_image
                              ?.product_display_image
                          }
                        />
                      )}
                      <Stack>
                        <Typography
                          component="span"
                          sx={{
                            fontSize: font.size.sm,
                            color: Black,
                            fontWeight: 500,
                          }}
                        >
                          {option?.item_name ? option?.item_name : "ไม่มีชื่อ"}
                        </Typography>
                        <Typography
                          component="span"
                          sx={{
                            fontSize: font.size.sm,
                            color: DarkGrey,
                          }}
                        >
                          {option?.item_code ? option?.item_code : "-"}
                        </Typography>
                      </Stack>
                    </Box>
                    <Box
                      sx={{
                        gridColumn: "4 / 6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontSize: font.size.sm,
                          color: DarkGrey,
                        }}
                      >
                        {option?.user?.seller?.company_name}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        gridColumn: "6 / 8",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontSize: font.size.sm,
                          color: DarkGrey,
                        }}
                      >
                        {option?.category?.cate_th}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        gridColumn: "8 / 10",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Stack>
                        <Typography
                          component="span"
                          sx={{
                            fontSize: font.size.sm,
                            color: DarkGrey,
                          }}
                        >
                          {option?.product_choices?.size?.quantity === "size"
                            ? `${option?.product_choices?.size?.width} ${
                                getLocale() === "th"
                                  ? option?.product_choices?.size?.unit_width
                                      ?.unit_th
                                  : option?.product_choices?.size?.unit_width
                                      ?.unit_en
                              } x ${option?.product_choices?.size?.height} ${
                                getLocale() === "th"
                                  ? option?.product_choices?.size?.unit_height
                                      ?.unit_th
                                  : option?.product_choices?.size?.unit_height
                                      ?.unit_en
                              } x ${option?.product_choices?.size?.thickness} ${
                                getLocale() === "th"
                                  ? option?.product_choices?.size
                                      ?.unit_thickness?.unit_th
                                  : option?.product_choices?.size
                                      ?.unit_thickness?.unit_en
                              }`
                            : option?.product_choices?.size?.quantity ===
                                "capacity"
                              ? `${
                                  option?.product_choices?.size?.capacity_size
                                } ${
                                  getLocale() === "th"
                                    ? option?.product_choices?.size
                                        ?.unit_capacity?.unit_th
                                    : option?.product_choices?.size
                                        ?.unit_capacity?.unit_en
                                }`
                              : getLocale() === "th"
                                ? "สินค้าสั่งทำพิเศษ"
                                : "Made by order"}
                        </Typography>

                        <Typography
                          component="span"
                          sx={{
                            fontSize: font.size.sm,
                            color: DarkGrey,
                          }}
                        >
                          {option?.product_choices?.name !== ""
                            ? `( ${option?.product_choices?.name} )`
                            : `-`}
                        </Typography>
                      </Stack>
                    </Box>
                    <Box
                      sx={{
                        gridColumn: "10 / 12",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Stack alignItems="flex-end">
                        {option?.product_choices?.group_price?.special_price <
                          option?.product_choices?.group_price?.price &&
                          option?.product_choices?.group_price
                            ?.special_price !== 0 &&
                          option?.product_choices?.group_price
                            ?.special_price !== null && (
                            <Typography
                              component="span"
                              sx={{
                                fontSize: font.size.xs,
                                color: DarkGrey,
                                fontWeight: 400,
                                textDecoration: "line-through",
                              }}
                            >
                              {convertPrice(
                                option?.product_choices?.group_price?.price,
                              )}
                            </Typography>
                          )}
                        <Typography
                          component="span"
                          sx={{
                            fontSize: font.size.md,
                            color: Black,
                            fontWeight: 500,
                          }}
                        >
                          {convertPrice(
                            option?.product_choices?.group_price
                              ?.is_start_price === true
                              ? option?.product_choices?.group_price
                                  ?.start_price
                              : option?.product_choices?.group_price
                                    .special_price > 0 &&
                                  option?.product_choices?.group_price
                                    .special_price !== null &&
                                  option?.product_choices?.group_price
                                    .special_price !== undefined
                                ? option?.product_choices?.group_price
                                    .special_price
                                : option?.product_choices?.group_price?.price,
                          )}
                        </Typography>
                      </Stack>
                    </Box>
                    <Box
                      sx={{
                        gridColumn: "12 / 13",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Button
                        variant="secondary"
                        size="large"
                        sx={{
                          minWidth: 160,
                          maxWidth: 350,
                        }}
                        startIcon={<AddIcon />}
                        onClick={() => addProduct(option)}
                        disabled={
                          selectedProducts.findIndex(
                            (item) => item?.product_choices?._id === option._id,
                          ) !== -1
                        }
                      >
                        เพิ่มสินค้า
                      </Button>
                    </Box>
                  </S.ProductOptionContainer>
                </li>
              );
            }}
            options={productList}
            value={null}
            getOptionLabel={(option) => {
              return (
                option?.item_code +
                " - " +
                option?.item_name +
                (option.name !== "" ? ` - (` + option.name + `)` : ``) +
                " - " +
                option.user.seller.company_name
              );
            }}
            filterOptions={createFilterOptions({
              matchFrom: "any",
              limit: 100,
            })}
            sx={{
              mt: 2,
              // mb: 3,
              width: "100%",
              background: "#fff",
              borderRadius: "16px",
            }}
            renderInput={(params) => (
              <TextField
                className="TextField-without-border-radius"
                {...params}
                label="ค้นหาโดย ชื่อสินค้า / ชื่อร้านค้า"
              />
            )}
          />
          <Typography
            variant="body1"
            sx={{
              alignSelf: "flex-start",
              my: 2,
              color: DarkGrey,
              fontSize: font.size.sm,
            }}
          >
            {`สินค้า ${selectedProducts.length}/${MAX_PRODUCT_SUGGESTION} รายการ`}
          </Typography>
          <Box
            style={{
              width: "100%",
              maxHeight: 500,
              overflowY: "auto",
            }}
          >
            <DraggableBoard
              items={selectedProducts}
              onDragEnd={({ active, over }) => {
                if (active.id !== over?.id) {
                  setSelectedProducts((prev) => {
                    const oldIndex = prev.findIndex(
                      (item) => item._id === active.id,
                    );
                    const newIndex = prev.findIndex(
                      (item) => item._id === over.id,
                    );

                    const newReArranged = arrayMove(prev, oldIndex, newIndex);
                    // moveBannerPosition(newReArranged);

                    return newReArranged;
                  });
                }
              }}
              Components={{
                DroppableParent: ({ items, children }) => {
                  return (
                    <S.ProductSuggestionContainer>
                      {children({ items })}
                    </S.ProductSuggestionContainer>
                  );
                },
                DraggableItem: (props) => {
                  const { item: product, items, ...rest } = props; // Destructuring items to prevent this spreading to html element
                  return (
                    <WrapperProductCard {...rest}>
                      <ProductCard
                        id={product?.product_choices?._id}
                        brand={product?.user?.seller?.brand_name}
                        price={product?.product_choices?.group_price}
                        src={
                          product?.product_choices?.group_image
                            ?.product_display_image
                        }
                        srcHover={
                          product?.product_choices?.group_image
                            ?.product_closeup_image
                        }
                        itemcode={product?.item_code}
                        itemname={product?.item_name}
                        startprice={
                          product?.product_choices?.group_price?.start_price
                        }
                        specialprice={
                          product?.product_choices?.group_price?.special_price
                        }
                        mode={
                          product?.product_choices?.group_price?.is_start_price
                        }
                        cate={product?.category?.cate_th}
                        view={product?.product_choices?.view_count}
                        choice={0}
                        partnername={product?.user?.seller?.company_name}
                        choice_id={product?.product_choices?._id}
                        discount={
                          product?.product_choices?.group_price?.reduce_percent
                            ? product?.product_choices?.group_price?.reduce_percent?.toFixed(
                                0,
                              )
                            : null
                        }
                        sellerName={product?.user?.seller?.company_name}
                        sellerLogo={product?.user?.seller?.profile_image}
                        productType={product?.product_types
                          .map((data) => data.type_th)
                          .join(",")}
                        size={{ ...product?.product_choices?.size }}
                        isExclusiveDeal={product?.product_choices?.is_deal}
                        isDelivery={product?.user?.seller?.is_delivery}
                        isInstall={
                          product?.is_installer &&
                          product?.product_choices?.group_price
                            ?.install_price === 0
                        }
                        isSell={product?.user?.seller?.is_sell}
                        isInstock={product?.product_choices?.is_instock}
                      />
                      <S.HoverContainer>
                        <S.ItemBackdrop />
                        <S.ItemActionMenu>
                          <IconButton
                            sx={{
                              color: White,
                              backgroundColor: hexToRgba(White, 0.4),
                            }}
                            onClick={() => {
                              removeProduct(product._id);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </S.ItemActionMenu>
                      </S.HoverContainer>
                    </WrapperProductCard>
                  );
                },
              }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "center",
        }}
      >
        <Button
          variant="outlined"
          size="large"
          onClick={onClose}
          sx={{
            minWidth: 160,
          }}
        >
          ยกเลิก
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={() => {
            openDialog({
              type: "confirm",
              title: `คุณต้องการบันทึกการแก้ไขใช่หรือไม่?`,
              onConfirm: async () => {
                await handleSubmit();
              },
              maxWidth: 342,
            });
          }}
          sx={{
            minWidth: 160,
          }}
          autoFocus // enable auto focus to increase accessibility
        >
          ยืนยัน
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductSuggestionAddDialog;
